import React, { useEffect, useState } from 'react';
import '../../ForBrands/brandMode.css';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../../constants';
import {
  Badge,
  BmModal,
  Btn,
  Card,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Overline,
  PageHeader,
  SearchBox,
  Select,
  Toggle,
} from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import { POSITIVE_DOT, TD_CLASS, TD_MUTED_CLASS, TH_CLASS, TR_CLASS } from '../../admin/paymentStatus';

const PROMOCODES_URL = `${API_BASE_URL}/promocodes/admin/`;

// The standalone page (Admin Mode > More > Promo codes). Inside the
// Payments page's Promotions tab the manager draws no header of its own.
export const PROMOCODES_PATH = '/admin/promocodes';

const INTRO = 'Create, edit, and manage discount codes for your users. Track code usage and run targeted promotions.';

// bm-orange: the Full waiver badge's dot.
const BRAND_DOT = '#FF4400';

const COLUMNS = ['Code', 'Type', 'Value', 'Max uses', 'Used', 'Status', 'Valid from', 'Valid to'];

const initialForm = {
  code: '',
  discount_type: '',
  discount_value: '',
  max_uses: '',
  valid_from: '',
  valid_to: '',
  is_active: true,
  // null until the admin sets the Full waiver box: the flag is then not
  // sent and the server's rule decides from the discount.
  waives_athlete_fee: null,
};

// Same rule as the server's promo_waives_athlete_fee: a stored flag decides;
// without one, a code with no discount type or a percent code of 100 or
// more is a full waiver.
export const waivesAthleteFee = (code) => {
  if (!code) return false;
  const flag = code.waives_athlete_fee;
  if (flag !== undefined && flag !== null) return flag === true;
  const kind = String(code.discount_type || '').trim().toLowerCase();
  if (!kind) return true;
  return kind === 'percent' && Number(code.discount_value) >= 100;
};

// The Full waiver box in the form: the admin's own setting (or the flag the
// code stores) when there is one, else what the server's rule derives from
// the discount as typed so far. Off until a code type is picked.
export const waiverBoxChecked = (form) => {
  if (!form) return false;
  if (typeof form.waives_athlete_fee === 'boolean') return form.waives_athlete_fee;
  if (!form.discount_type) return false;
  return waivesAthleteFee({ discount_type: form.discount_type, discount_value: form.discount_value });
};

// Stored window dates are UTC. The list returns them as ISO strings, with a
// zone or without one (Mongo's naive UTC); a string with no zone is UTC.
const parseStoredDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const hasZone = /(?:[zZ]|[+-]\d\d:?\d\d)$/.test(value);
  const d = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad2 = (n) => String(n).padStart(2, '0');

// A stored date as a datetime-local value (the admin's local time).
export const toLocalInput = (value) => {
  const d = parseStoredDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// A datetime-local value (local time) as a UTC ISO string, or null.
export const toUtcIso = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// The body for a create (POST) or an edit (PUT). Numbers go as numbers,
// dates as UTC ISO strings, flags as booleans. used_count is never sent (the
// redeem counter moves only on redemptions), nor are the Stripe ids and
// created_by the list returns. Empty values are left out, and so is
// waives_athlete_fee until the admin sets it: a code without the flag keeps
// following its discount on the server (100% or more waives the fee).
export const buildPromoPayload = (form) => {
  const tracking = form.discount_type === 'tracking';
  const maxUses = toNumber(form.max_uses);
  const payload = {
    code: form.code,
    discount_type: form.discount_type,
    discount_value: tracking ? 0 : toNumber(form.discount_value),
    max_uses: tracking ? 0 : (maxUses === null ? null : Math.trunc(maxUses)),
    valid_from: toUtcIso(form.valid_from),
    valid_to: toUtcIso(form.valid_to),
    is_active: !!form.is_active,
    waives_athlete_fee: typeof form.waives_athlete_fee === 'boolean' ? form.waives_athlete_fee : null,
  };
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined));
};

// An existing code as the edit form's state. A stored waiver flag is kept
// (read as the server reads it: only true waives); a code without one gets
// null, so saving it does not turn the derived value into a stored flag.
const formFromCode = (c) => ({
  code: c.code,
  discount_type: c.discount_type || '',
  discount_value: c.discount_value ?? '',
  max_uses: c.max_uses ?? '',
  valid_from: toLocalInput(c.valid_from),
  valid_to: toLocalInput(c.valid_to),
  is_active: c.is_active !== false,
  waives_athlete_fee: c.waives_athlete_fee === undefined || c.waives_athlete_fee === null
    ? null
    : c.waives_athlete_fee === true,
});

// The first message out of a DRF error body ({field: [msg]} or {error}).
const firstError = (data, fallback) => {
  if (!data || typeof data !== 'object') return fallback;
  if (typeof data.error === 'string') return data.error;
  const first = Object.values(data).find((v) => (Array.isArray(v) ? v.length : typeof v === 'string'));
  if (Array.isArray(first)) return String(first[0]);
  return typeof first === 'string' ? first : fallback;
};

const PromoCodeManager = () => {
  const token = useSelector((state) => state.auth.token);
  const user = token ? JSON.parse(atob(token.split('.')[1])) : {};
  const isAdmin = user.permissions && user.permissions.includes('IS_ADMIN');
  const { pathname = '' } = useLocation() || {};
  const standalone = pathname.replace(/\/+$/, '') === PROMOCODES_PATH;

  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editIdx, setEditIdx] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(PROMOCODES_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCodes(res.data);
    } catch (err) {
      setError('Failed to fetch promo codes');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreateModal = () => {
    setEditIdx(null);
    setForm(initialForm);
    setShowModal(true);
    setError('');
  };

  // Takes the row's code, not its index: the rows are the filtered list, so
  // an index into `codes` opened the wrong code while a search was active.
  const openEditModal = (code) => {
    setEditIdx(code.code);
    setForm(formFromCode(code));
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditIdx(null);
    setForm(initialForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(PROMOCODES_URL, buildPromoPayload(form), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Saved successfully.');
      closeModal();
      fetchCodes();
    } catch (err) {
      setError(firstError(err.response?.data, 'Failed to create promo code'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.put(PROMOCODES_URL, buildPromoPayload(form), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Saved successfully.');
      closeModal();
      fetchCodes();
    } catch (err) {
      setError(firstError(err.response?.data, 'Failed to update promo code'));
    }
  };

  // The row's delete button opens the ConfirmModal; the delete runs on
  // confirm.
  const handleDelete = (code) => {
    setError('');
    setDeleteTarget(code);
  };

  const confirmDelete = async () => {
    const code = deleteTarget;
    setDeleting(true);
    try {
      await axios.delete(PROMOCODES_URL, {
        headers: { Authorization: `Bearer ${token}` },
        data: { code },
      });
      toast.success('Promo code deleted.');
      fetchCodes();
    } catch (err) {
      setError('Failed to delete promo code');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const filteredCodes = codes.filter((c) =>
    String(c.code || '').toLowerCase().includes(search.toLowerCase())
    || String(c.discount_type || '').toLowerCase().includes(search.toLowerCase()));

  if (!isAdmin) return null;

  const tracking = form.discount_type === 'tracking';

  return (
    <div className="flex flex-col gap-5">
      {standalone ? (
        <PageHeader eyebrow="Admin Mode" title="Promo codes" subtitle={INTRO} />
      ) : (
        <p className="max-w-2xl bm-body-m text-bm-muted">{INTRO}</p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search by code or type..."
          aria-label="Search promo codes"
          className="w-full sm:max-w-sm"
        />
        <Btn className="gap-2" onClick={openCreateModal}>
          <Icon name="plus" size={16} />
          New promo code
        </Btn>
      </div>

      {error && !showModal && <ErrorState>{error}</ErrorState>}

      <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-hidden">
        {loading ? (
          <Loading />
        ) : filteredCodes.length === 0 ? (
          <EmptyState className="m-6">No promo codes found. Create one to start tracking redemptions.</EmptyState>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-bm-subtle">
                <tr>
                  {COLUMNS.map((h) => (
                    <th key={h} className={TH_CLASS}>{h}</th>
                  ))}
                  <th className={`${TH_CLASS} !text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map((c) => (
                  <tr key={c.code} className={TR_CLASS}>
                    <td className={TD_CLASS}>
                      <span className="font-mono font-bold tracking-wider">{c.code}</span>
                    </td>
                    <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                      <span className="capitalize">{c.discount_type}</span>
                      {waivesAthleteFee(c) && (
                        <Badge tone="outline" dot={BRAND_DOT} className="ml-2">Full waiver</Badge>
                      )}
                    </td>
                    <td className={TD_CLASS}>
                      {c.discount_type === 'tracking'
                        ? <span className="text-bm-tertiary">n/a</span>
                        : <span className="bm-label-l">{c.discount_type === 'percent' ? `${c.discount_value}%` : `$${c.discount_value}`}</span>}
                    </td>
                    <td className={TD_MUTED_CLASS}>
                      {Number(c.max_uses) === 0
                        ? <span className="bm-label-l text-bm-ink">∞</span>
                        : c.max_uses}
                    </td>
                    <td className={TD_MUTED_CLASS}>{c.used_count ?? 0}</td>
                    <td className={TD_CLASS}>
                      {c.is_active !== false
                        ? <Badge tone="outline" dot={POSITIVE_DOT}>Active</Badge>
                        : <Badge tone="passive">Inactive</Badge>}
                    </td>
                    <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                      {toLocalInput(c.valid_from).replace('T', ' ')}
                    </td>
                    <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                      {toLocalInput(c.valid_to).replace('T', ' ')}
                    </td>
                    <td className={TD_CLASS}>
                      <div className="flex items-center justify-center gap-1">
                        <Btn
                          variant="ghost"
                          size="sm"
                          className="!px-2"
                          onClick={() => openEditModal(c)}
                          aria-label={`Edit ${c.code}`}
                          title="Edit"
                        >
                          <Icon name="edit" size={15} />
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          className="!px-2"
                          onClick={() => handleDelete(c.code)}
                          aria-label={`Delete ${c.code}`}
                          title="Delete"
                        >
                          <Icon name="trash" size={15} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showModal && (
        <BmModal title={editIdx === null ? 'New promo code' : 'Edit promo code'} onClose={closeModal}>
          <form onSubmit={editIdx === null ? handleSubmit : handleUpdate} className="flex flex-col gap-5">
            {/* 1) The code itself + on/off status */}
            <div className="flex items-end gap-4">
              <Field label="Code" className="flex-1">
                <Input
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="e.g. SUMMER25"
                  required
                  disabled={editIdx !== null}
                  className="font-mono tracking-wider disabled:bg-bm-subtle disabled:text-bm-muted"
                />
              </Field>
              <div className="flex h-10 shrink-0 items-center gap-2">
                <Toggle
                  on={!!form.is_active}
                  onChange={(on) => setForm((prev) => ({ ...prev, is_active: on }))}
                  label="Active"
                />
                <span className="bm-label-l text-bm-ink">{form.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>

            {/* 2) Discount */}
            <div className="flex flex-col gap-2">
              <Overline>Discount</Overline>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code type">
                  <Select
                    name="discount_type"
                    value={form.discount_type}
                    required
                    onChange={(e) => {
                      const next = e.target.value;
                      // Tracking codes carry no discount and are always
                      // unlimited (max_uses=0); percent/fixed codes require
                      // the admin to enter an explicit cap.
                      setForm((prev) => ({
                        ...prev,
                        discount_type: next,
                        discount_value: next === 'tracking' ? 0 : prev.discount_value,
                        max_uses: next === 'tracking' ? 0 : '',
                      }));
                    }}
                  >
                    <option value="" disabled>Select code type…</option>
                    <option value="percent">Percent discount</option>
                    <option value="fixed">Fixed-amount discount</option>
                    <option value="tracking">Tracking only (no discount)</option>
                  </Select>
                </Field>
                <Field
                  label={tracking
                    ? 'Value'
                    : form.discount_type === 'percent'
                      ? 'Value (%)'
                      : 'Value ($)'}
                >
                  <Input
                    name="discount_value"
                    value={tracking ? 0 : form.discount_value}
                    onChange={handleChange}
                    placeholder={form.discount_type === 'percent' ? 'e.g. 25' : form.discount_type === 'fixed' ? 'e.g. 10.00' : tracking ? 'n/a' : 'Pick a code type first'}
                    type="number"
                    required={!tracking}
                    disabled={tracking}
                    className="disabled:bg-bm-subtle disabled:text-bm-tertiary"
                  />
                </Field>
              </div>
              {tracking && (
                <p className="rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2 bm-body-s text-bm-muted">
                  Tracking codes don't apply a discount at checkout. Use them
                  to attribute signups and referrals. Usage is counted locally.
                </p>
              )}
            </div>

            {/* 3) Athlete fee waiver */}
            <div className="flex flex-col gap-2">
              <Overline>Athlete fee</Overline>
              <div className="rounded-[8px] border border-bm-border px-4 py-3">
                <Checkbox
                  checked={waiverBoxChecked(form)}
                  onChange={(on) => setForm((prev) => ({ ...prev, waives_athlete_fee: on }))}
                  label="Full waiver"
                />
                <p className="mt-1 pl-[30px] bm-body-s text-bm-muted">
                  Athletes who enter this code in the app get Premium free until the Valid to date. Leave it off for a discount at checkout. On by default for 100% codes.
                </p>
              </div>
            </div>

            {/* 4) Usage limit */}
            <div className="flex flex-col gap-2">
              <Overline>Usage limit</Overline>
              {tracking ? (
                <div className="flex items-center gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2.5 bm-body-m text-bm-muted">
                  <span className="bm-label-l text-bm-ink">∞</span>
                  Unlimited, tracking codes always allow unlimited redemptions.
                </div>
              ) : (
                <Field label="Max uses">
                  <Input
                    name="max_uses"
                    value={form.max_uses}
                    onChange={handleChange}
                    placeholder="e.g. 100"
                    type="number"
                    min="1"
                    required
                  />
                </Field>
              )}
            </div>

            {/* 5) Validity window */}
            <div className="flex flex-col gap-2">
              <Overline>Validity window</Overline>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Valid from">
                  <Input name="valid_from" value={form.valid_from} onChange={handleChange} type="datetime-local" required />
                </Field>
                <Field label="Valid to">
                  <Input name="valid_to" value={form.valid_to} onChange={handleChange} type="datetime-local" required />
                </Field>
              </div>
            </div>

            {error && <ErrorState>{error}</ErrorState>}

            <div className="-mx-7 -mb-6 flex items-center justify-end gap-3 border-t border-bm-border px-7 pb-5 pt-4">
              <Btn variant="secondary" onClick={closeModal}>
                Cancel
              </Btn>
              <Btn type="submit">
                {editIdx === null ? 'Create' : 'Update'}
              </Btn>
            </div>
          </form>
        </BmModal>
      )}

      {deleteTarget && (
        <ConfirmModal
          tone="danger"
          title="Delete promo code"
          body={`The promo code ${deleteTarget} will be deleted and can no longer be redeemed.`}
          confirmLabel="Delete"
          busy={deleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default PromoCodeManager;
